import React, { useEffect, useState, useCallback } from 'react';
import { searchWeatherLocations } from '../../services/contentPlugins';
import { buildWeatherPreviewHtml } from '../../utils/pluginPreviewHtml';
import PluginRenderingBar from './PluginRenderingBar';
import './ContentPlugins.css';

const RENDERING_MESSAGE = 'Generating your plugin image… You can close this window — we\'ll keep working in the background.';

export default function WeatherPluginConfig({
  open,
  onClose,
  onSave,
  userId,
  screenId,
  token,
  initialConfig,
  saving,
  rendering = false,
}) {
  const [query, setQuery] = useState('');
  const [locations, setLocations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (open && initialConfig?.locationName) {
      setSelected({
        label: initialConfig.locationName,
        latitude: initialConfig.latitude,
        longitude: initialConfig.longitude,
      });
      setQuery(initialConfig.locationName);
    }
  }, [open, initialConfig]);

  useEffect(() => {
    if (!open) {
      setLocations([]);
      if (!rendering) {
        setQuery('');
        setSelected(null);
      }
    }
  }, [open, rendering]);

  const loadPreview = useCallback(() => {
    setPreviewHtml(buildWeatherPreviewHtml({
      locationName: selected?.label || query.trim() || 'Your City',
      units: 'fahrenheit',
    }));
  }, [selected, query]);

  useEffect(() => {
    if (!open) return undefined;
    loadPreview();
    return undefined;
  }, [open, loadPreview]);

  useEffect(() => {
    if (query.length < 2 || rendering) {
      setLocations([]);
      return undefined;
    }
    const t = setTimeout(() => {
      setSearching(true);
      searchWeatherLocations(query, token)
        .then((res) => setLocations(res.data.locations || []))
        .catch(() => setLocations([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [query, token, rendering]);

  if (!open) return null;

  const readOnly = rendering;

  const handleSave = () => {
    if (!selected || readOnly) return;
    onSave({
      locationName: selected.label,
      latitude: selected.latitude,
      longitude: selected.longitude,
      units: 'fahrenheit',
    });
  };

  return (
    <div className="contentPluginOverlay" role="presentation">
      <div
        className={`contentPluginConfigModal ${readOnly ? 'contentPluginConfigReadOnly' : ''}`}
        role="dialog"
        aria-modal="true"
      >
        <div className="contentPluginConfigHeader">
          <h2>Weather Plugin</h2>
          <button type="button" className="contentPluginClose" onClick={onClose}>×</button>
        </div>
        {rendering ? (
          <PluginRenderingBar message={RENDERING_MESSAGE} />
        ) : null}
        <div className="contentPluginConfigBody">
          <div className="contentPluginForm">
            <label htmlFor="weather-search">Location</label>
            <input
              id="weather-search"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search city..."
              className="contentPluginInput"
              disabled={readOnly}
              readOnly={readOnly}
            />
            {searching && !readOnly && <p className="contentPluginHint">Searching...</p>}
            {locations.length > 0 && !readOnly && (
              <ul className="contentPluginLocationList">
                {locations.map((loc) => (
                  <li key={`${loc.latitude}-${loc.longitude}`}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelected({ label: loc.label, latitude: loc.latitude, longitude: loc.longitude });
                        setQuery(loc.label);
                        setLocations([]);
                      }}
                    >
                      {loc.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {selected && <p className="contentPluginSelected">Selected: {selected.label}</p>}
          </div>
          <div className="contentPluginPreviewPane">
            <p className="contentPluginPreviewLabel">Preview</p>
            <div className="contentPluginPreviewFrameWrap">
              {previewHtml ? (
                <iframe
                  title="Weather preview"
                  className="contentPluginPreviewFrame"
                  srcDoc={previewHtml}
                  sandbox="allow-scripts"
                />
              ) : (
                <div className="contentPluginPreviewEmpty">Loading preview…</div>
              )}
            </div>
          </div>
        </div>
        <div className={`contentPluginConfigFooter ${rendering ? 'contentPluginConfigFooterRendering' : ''}`}>
          {rendering ? (
            <p className="contentPluginRenderingHint">
              Reopen this slot anytime to check progress. Editing unlocks when the video is ready.
            </p>
          ) : null}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" className="contentPluginCancel" onClick={onClose}>
              {rendering ? 'Close' : 'Cancel'}
            </button>
            {!rendering ? (
              <button
                type="button"
                className="contentPluginSave"
                disabled={!selected || saving}
                onClick={handleSave}
              >
                {saving ? 'Starting…' : 'Save Weather Plugin'}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
